import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastContainer } from 'react-toastify';
import { HelmetProvider } from 'react-helmet-async';
import 'react-toastify/dist/ReactToastify.css';

// Layouts
import MainLayout from './layouts/MainLayout';
import DashboardLayout from './layouts/DashboardLayout';
import AdminLayout from './layouts/AdminLayout';

// Public Pages
import HomePage from './pages/Home/HomePage';
import TourDetailPage from './pages/TourDetail/TourDetailPage';
import LoginPage from './pages/Auth/LoginPage';
import RegisterPage from './pages/Auth/RegisterPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import AboutPage from './pages/About/AboutPage';
import ContactPage from './pages/Contact/ContactPage';
import BlogPage from './pages/Blog/BlogPage';
import BlogPostPage from './pages/Blog/BlogPostPage';
import ToursListingPage from './pages/Tours/ToursListingPage';
import BookingPage from './pages/Booking/BookingPage';
import SitemapPage from './pages/SEO/SitemapPage';

// Customer Dashboard Pages
import DashboardPage from './pages/Dashboard/DashboardPage';
import DashboardHome from './pages/Dashboard/components/DashboardHome';
import MyBookings from './pages/Dashboard/components/MyBookings';
import CustomerBookingDetail from './pages/Dashboard/components/CustomerBookingDetail';
import Favorites from './pages/Dashboard/components/Favorites';
import UserProfile from './pages/Dashboard/components/UserProfile';
import Settings from './pages/Dashboard/components/Settings';

// Admin Pages
import AdminDashboardPage from './pages/Admin/Dashboard/DashboardPage';
import AdminLoginPage from './pages/Admin/Auth/AdminLoginPage';
import LoginTest from './pages/Admin/Auth/LoginTest';

// Admin Tours Pages
import AdminToursPage from './pages/Admin/Tours/ToursPage';
import TourForm from './pages/Admin/Tours/TourForm';
import ImageDebugger from './pages/Admin/Tours/ImageDebugger';

// Admin Locations Pages
import LocationsPage from './pages/Admin/Locations/LocationsPage';

// Admin Categories Pages
import CategoriesPage from './pages/Admin/Categories/CategoriesPage';

// Admin Bookings Pages
import AdminBookingsPage from './pages/Admin/Bookings/BookingsPage';
import BookingDetail from './pages/Admin/Bookings/BookingDetail';
import DirectBookingsTest from './pages/Admin/Bookings/DirectBookingsTest';
import ApiComparisonTool from './pages/Admin/Bookings/ApiComparisonTool';

// Admin Customers Pages
import AdminCustomersPage from './pages/Admin/Customers/CustomersPage';
import CustomerDetail from './pages/Admin/Customers/CustomerDetail';

// Admin Vehicles Pages
import AdminVehiclesPage from './pages/Admin/Vehicles/VehiclesPage';
import VehicleDetail from './pages/Admin/Vehicles/VehicleDetail';
import VehicleForm from './pages/Admin/Vehicles/VehicleForm';
import VehicleApiTest from './pages/Admin/Vehicles/VehicleApiTest';
import VehicleCreateTest from './pages/Admin/Vehicles/VehicleCreateTest';
import VehicleUtilizationDashboard from './pages/Admin/Vehicles/VehicleUtilizationDashboard';
import VehicleMaintenanceForm from './pages/Admin/Vehicles/VehicleMaintenanceForm';
import VehicleExpenseForm from './pages/Admin/Vehicles/VehicleExpenseForm';
import VehicleApiDebug from './components/VehicleApiDebug';
import AuthDebug from './components/AuthDebug';
import ApiDebugTool from './components/ApiDebugTool';
import TestVehicleCreator from './components/TestVehicleCreator';
import VehicleDataDebugger from './components/VehicleDataDebugger';

// Admin Payments Pages
import AdminPaymentsPage from './pages/Admin/Payments/PaymentsPage';
import PaymentDetail from './pages/Admin/Payments/PaymentDetail';

// Admin Reports Pages
import AdminReportsPage from './pages/Admin/Reports/ReportsPage';
import RevenueReport from './pages/Admin/Reports/RevenueReport';
import BookingsReport from './pages/Admin/Reports/BookingsReport';
import CustomersReport from './pages/Admin/Reports/CustomersReport';
import ToursReport from './pages/Admin/Reports/ToursReport';

// Admin Blog Pages
import AdminBlogPostsPage from './pages/Admin/BlogPosts/BlogPostsPage';
import BlogPostDetail from './pages/Admin/BlogPosts/BlogPostDetail';
import BlogPostForm from './pages/Admin/BlogPosts/BlogPostForm';

// Admin Settings Page
import AdminSettingsPage from './pages/Admin/Settings/SettingsPage';

// Admin Contact Messages Pages
import { ContactMessageList, ContactMessageDetail } from './pages/Admin/ContactMessages';

// Guards
import PrivateRoute from './components/Guards/PrivateRoute';
import AdminRoute from './components/Guards/AdminRoute';

// CSS
import './App.css';

// Placeholder components for missing pages
const PaymentPage = () => <div className="p-8"><h1 className="text-2xl font-bold">Payment Page</h1><p>This is a placeholder for the Payment Page.</p></div>;

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <ToastContainer position="top-right" autoClose={5000} />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<MainLayout />}>
            <Route index element={<HomePage />} />
            <Route path="tours" element={<ToursListingPage />} />
            <Route path="tours/category/:id" element={<ToursListingPage />} />
            <Route path="tours/:slug" element={<TourDetailPage />} />
            <Route path="booking/:tourId" element={<BookingPage />} />
            <Route path="payment/:bookingId" element={<PaymentPage />} />
            <Route path="blog" element={<BlogPage />} />
            <Route path="blog/:slug" element={<BlogPostPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="login" element={<LoginPage />} />
            <Route path="register" element={<RegisterPage />} />
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
            <Route path="reset-password/:token" element={<ResetPasswordPage />} />
          </Route>
          
          {/* SEO Routes */}
          <Route path="sitemap.xml" element={<SitemapPage />} />
          <Route path="robots.txt" element={<SitemapPage />} />
        
        
        {/* Customer Dashboard Routes */}
        <Route 
          path="/dashboard" 
          element={
            <PrivateRoute>
              <DashboardLayout>
                <DashboardPage />
              </DashboardLayout>
            </PrivateRoute>
          }
        >
          <Route index element={<DashboardHome />} />
          <Route path="bookings" element={<MyBookings />} />
          <Route path="bookings/:bookingId" element={<CustomerBookingDetail />} />
          <Route path="profile" element={<UserProfile />} />
          <Route path="favorites" element={<Favorites />} />
          <Route path="saved-tours" element={<Favorites />} />
          <Route path="settings" element={<Settings />} />
        </Route>

        {/* Admin Login Route */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin/login-test" element={<LoginTest />} />
        <Route path="/admin/vehicle-api-test" element={<VehicleApiTest />} />
        <Route path="/admin/vehicle-create-test" element={<VehicleCreateTest />} />
        <Route path="/admin/vehicle-api-debug" element={<VehicleApiDebug />} />
        <Route path="/admin/auth-debug" element={<AuthDebug />} />
        <Route path="/api-debug" element={<ApiDebugTool />} />
        <Route path="/test-vehicle-creator" element={<TestVehicleCreator />} />
        <Route path="/vehicle-data-debugger" element={<VehicleDataDebugger />} />

        {/* Admin Routes */}
        <Route 
          path="/admin" 
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<AdminDashboardPage />} />
          
          {/* Tours Management */}
          <Route path="tours" element={<AdminToursPage />} />
          <Route path="tours/new" element={<TourForm />} />
          <Route path="tours/edit/:id" element={<TourForm />} />
          <Route path="tours/image-debugger" element={<ImageDebugger />} />
          
          {/* Locations Management */}
          <Route path="locations" element={<LocationsPage />} />
          
          {/* Categories Management */}
          <Route path="categories" element={<CategoriesPage />} />
          
          {/* Bookings Management */}
          <Route path="bookings" element={<AdminBookingsPage />} />
          <Route path="bookings/:id" element={<BookingDetail />} />
          <Route path="bookings-test" element={<DirectBookingsTest />} />
          <Route path="api-comparison" element={<ApiComparisonTool />} />
          
          {/* Customers Management */}
          <Route path="customers" element={<AdminCustomersPage />} />
          <Route path="customers/:id" element={<CustomerDetail />} />
          
          {/* Vehicles Management */}
          <Route path="vehicles" element={<AdminVehiclesPage />} />
          <Route path="vehicles/new" element={<VehicleForm />} />
          <Route path="vehicles/:id" element={<VehicleDetail />} />
          <Route path="vehicles/edit/:id" element={<VehicleForm />} />
          <Route path="vehicles/utilization" element={<VehicleUtilizationDashboard />} />
          <Route path="vehicles/:id/maintenance/add" element={<VehicleMaintenanceForm />} />
          <Route path="vehicles/:id/expense/add" element={<VehicleExpenseForm />} />
          
          {/* Payments Management */}
          <Route path="payments" element={<AdminPaymentsPage />} />
          <Route path="payments/:id" element={<PaymentDetail />} />
          
          {/* Reports Management */}
          <Route path="reports" element={<AdminReportsPage />} />
          <Route path="reports/revenue" element={<RevenueReport />} />
          <Route path="reports/bookings" element={<BookingsReport />} />
          <Route path="reports/customers" element={<CustomersReport />} />
          <Route path="reports/tours" element={<ToursReport />} />
          
          {/* Blog Management */}
          <Route path="blog" element={<AdminBlogPostsPage />} />
          <Route path="blog/new" element={<BlogPostForm />} />
          <Route path="blog/:id" element={<BlogPostDetail />} />
          <Route path="blog/edit/:id" element={<BlogPostForm />} />
          
          {/* Settings */}
          <Route path="settings" element={<AdminSettingsPage />} />
          
          {/* Contact Messages Management */}
          <Route path="contact-messages" element={<ContactMessageList />} />
          <Route path="contact-messages/:id" element={<ContactMessageDetail />} />
        </Route>
      </Routes>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
